import { Injectable } from '@angular/core';
const get: any = require('lodash.get');
const platform: any = require('platform');
const slug: any = require('slug');
import {get as getCookie, set as setCookie, remove as removeCookie} from 'js-cookie';
declare const document: any;
declare const window: any;

@Injectable()
export class BrandService {
  private _get: any  = get;
  public _brandData: any;
  private _linkIdCounter = 0;
  public _scriptIdCounter = 0;
  public _browserName = '';
  public _browserVersion = '';
  public _browserOS = '';
  private _deviceOrientation = '';
  private _deviceWidth = '';
  private _deviceType = '';
  private _breakpoints: Array<string> = ['768px', '992px', '1200px', '1920px'];
  private _breakpointsClasses: Array<string> = ['sm', 'md', 'lg', 'xl'];
  private _breakpointsDeviceTypes: Array<string> = ['mobile', 'tablet', 'desktop', 'large-screen'];
  private _breakpointsDefaultDeviceType = 'mobile';
  private _breakpointsDefaultClass = 'xs';
  private _defaultOptions: any;
  public slugify = slug;
  public getCookie = getCookie;
  public setCookie = setCookie;
  public removeCookie = removeCookie;
  public options: any = {};
  public emailValidationPattern = /^(?:[A-Za-z0-9._%+-]|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,61})+$/;
  public emailValidationPatternWithoutUsername = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,61}$/;
  constructor() {
    this._defaultOptions = {
      breakpoints: this._breakpoints,
      classes: this._breakpointsClasses,
      deviceTypes: this._breakpointsDeviceTypes,
      defaultClass: this._breakpointsDefaultClass,
      defaultDeviceType: this._breakpointsDefaultDeviceType
    };
  }

  insertScriptFromSrc(src: string | Array < string > ): void {
    if (!src) {
      return;
    }
    if (Array.isArray(src)) {
      src.forEach((url) => {
        this.insertScriptFromSrc(url);
      });
      return;
    }
    this._scriptIdCounter++;
    const scriptTag = document.createElement('script');
    scriptTag.id = `dui-script-${this._scriptIdCounter}`;
    scriptTag.src = src;
    document.head.appendChild(scriptTag);
  }


  insertLinkFromHref(href: string | Array < string > ): void {
    if (!href) {
      return;
    }
    if (Array.isArray(href)) {
      href.forEach((url) => {
        this.insertLinkFromHref(url);
      });
      return;
    }
    this._linkIdCounter++;
    const linkTag = document.createElement('link');
    linkTag.id = `dui-link-${this._linkIdCounter}`;
    linkTag.rel = 'stylesheet';
    linkTag.type = 'text/css';
    linkTag.href = href;
    document.head.appendChild(linkTag);
  }

  setBrandInfo(brandData: any): void {
    this._brandData = Object.assign({}, brandData);
  }

  getBrandInfo(): any {
    return this._brandData;
  }

  getBrandNameSlug(): string {
    return this.slugify(this.getBrandInfoByKey('branding.brandName'), { lower: true });
  }

  getBrandInfoByKey(key: string): string | number | any {
    return this._get(this._brandData, key, `Brand data ${key} not found!`);
  }

  setOptions(options: any): void {
    this.options = Object.assign({}, this._defaultOptions, options || {});
  }

  getLanguage() {
    return this.getCookie('language');
  }

  setLanguage(language: string): void {
    this.setCookie('language', language);
    this.setBodyAttribute('language', language);
  }

  getProvince() {
    return this.getCookie('province');
  }

  setProvince(province: string): void {
    this.setCookie('province', province);
    this.setBodyAttribute('province', province);
  }

  setupBrand(brandData: any, options: any = {}): void {
    this.setOptions(options);
    this.setBrandInfo(brandData);
    this.setBrandNameAttr();
    this.setBrowserAttr();
    this.updateDeviceTypeAndOrientation();
    if (this._brandData && this._brandData.brandingtheme) {
      this._brandData.brandingtheme.forEach(brandingtheme => {
        this.insertLinkFromHref(brandingtheme.mainCSSLink);
        this.insertLinkFromHref(brandingtheme.fonts);
      });
    }
    // setup language and provinces
    if (this.getLanguage()) {
      this.setLanguage(this.getLanguage());
    }
    if (this.getProvince()) {
      this.setProvince(this.getProvince());
    }
    // Resize event binding
    window.addEventListener('resize', this.updateDeviceTypeAndOrientation.bind(this));
  }

  setBodyAttribute(attrName: string, attrValue: string): void {
    document.body.setAttribute(`dui-${attrName}`, attrValue);
  }

  setBrandNameAttr(): void {
    this.setBodyAttribute('brand', this.getBrandInfoByKey('branding.brandName'));
  }

  setDeviceOrientationAttr(): void {
    if (window.matchMedia('(orientation: landscape)').matches) {
      this._deviceOrientation = 'landscape';
    } else {
      this._deviceOrientation = 'portrait';
    }
    this.setBodyAttribute('device-orientation', this._deviceOrientation);
  }

  setDeviceWidthAndTypeAttr(): void {
    this._deviceWidth = this.options.defaultClass;
    this._deviceType = this.options.defaultDeviceType;
    this._breakpoints.forEach((breakpoint, index) => {
      if (window.matchMedia(`(min-width: ${breakpoint})`).matches) {
        this._deviceWidth = this.options.classes[index] ? this.options.classes[index] : '';
        this._deviceType = this.options.deviceTypes[index] ? this.options.deviceTypes[index] : '';
      }
    });

    this.setBodyAttribute('device-width', this._deviceWidth);
    this.setBodyAttribute('device-type', this._deviceType);
  }

  setBrowserInfo(): void {
    this._browserName = this.slugify(platform.name, {lower: true});
    this._browserVersion = platform.version.toString().split('.')[0];
    this._browserOS = this.slugify(platform.os.family, {lower: true});
  }

  setBrowserAttr(): void {
    this.setBrowserInfo();
    this.setBodyAttribute('device-browser', this._browserName);
    this.setBodyAttribute('device-browser-version', this._browserVersion);
  }

  addClass(classes: string): void {
    const classesArr = classes.split(' ');
    classesArr.forEach(cls => {
      if (cls) {
        document.body.classList.add(cls);
      }
    });
  }

  removeClass(classes: string): void {
    const classesArr = classes.split(' ');
    classesArr.forEach(cls => {
     if (cls) {
        document.body.classList.remove(cls);
      }
    });
  }

  getBodyTagClasses(): string {
    return [
      this._browserName,
      this._browserOS,
      this._deviceType,
      this._deviceWidth,
      this._deviceOrientation
    ].join(' ');
  }



  updateDeviceTypeAndOrientation() {
    this.removeClass(this.getBodyTagClasses());
    this.setDeviceWidthAndTypeAttr();
    this.setDeviceOrientationAttr();
    this.addClass(this.getBodyTagClasses());
  }

  getEmailValidator() {
    return this.emailValidationPattern;
  }

  getEmailValidatorWithoutUsername() {
    return this.emailValidationPatternWithoutUsername;
  }
}
